import { debounce } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { FaPlus } from 'react-icons/fa6';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import symbolApi from '../../APIs/symbols';
import {
  Button,
  PageHeader,
  Pagination,
  Table,
  Typography,
} from '../../designComponents';
import { TableDataType } from '../../designComponents/Table';
import { ADD_SYMBOL_ROUTE } from '../../routes/routePaths';
import Store from '../../store';
import { RootState } from '../../store/selectors';
import { humanReadableDate, requestAPI } from '../../utils';
import { MyParamsType } from '../Building';
import { PrinterContainerStyled } from './styles';

type ParamsType = {
  page: number;
  limit: number;
};

const Symbols = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  const symbolsData = useSelector((state: RootState) => state.symbols.data);
  const pagination = useSelector(
    (state: RootState) => state.symbols.pagination,
  );
  const getSymbolsDetails = async (params: MyParamsType) => {
    try {
      setLoading(true);
      await requestAPI(
        symbolApi.getSymbols({
          page: params.page,
          limit: params.limit,
          search: params.search ? params.search : '',
        }),
      );
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };
  useEffect(() => {
    getSymbolsDetails(pagination);
  }, []);
  const handleAddSymbol = () => navigate(ADD_SYMBOL_ROUTE);
  const tableData: TableDataType = {
    headings: [
      {
        key: 'date',
        label: 'Date',
      },
      {
        key: 'symbolsName',
        label: 'Symbols Name',
      },
      {
        key: 'uploadedSymbols',
        label: 'Uploaded Symbols',
      },
    ],
    body: Array.isArray(symbolsData)
      ? symbolsData?.map((symbols) => {
        return {
          id: symbols._id,
          date: humanReadableDate(symbols.createdAt),
          symbolsName: symbols.symbolName,
          uploadedSymbols: symbols.uploadSymbol?.map((url: any) => (
              <img
                key={url._id}
                src={url.url}
                style={{
                  width: '40px',
                  height: '40px',
                }}
                alt={symbols.symbolName}
              />
          )),
        };
      })
      : [],
  };

  const debouncedFunction = useCallback(
    debounce((myParams) => getSymbolsDetails(myParams), 500),
    [],
  );

  const handlePagination = (updatedParams: ParamsType) => {
    getSymbolsDetails(updatedParams);
    dispatch(Store.Actions.update('symbols.pagination', updatedParams));
  };

  const handleOnSearchTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSearch(value);
    debouncedFunction({ ...pagination, page: 1, search: value });
  };

  return (
    <PrinterContainerStyled>
      <PageHeader
        pageTitle={<Typography variant="title">Symbols and Shapes</Typography>}
        otherActions={
          <Button variant="secondary" size="sm" onClick={handleAddSymbol}>
            <FaPlus /> Add Symbol
          </Button>
        }
        handleOnSearchTextChange={handleOnSearchTextChange}
        search={search}
      />

      <div className="table-container">
        <Table
          tableData={tableData}
          isLoading={loading}
          maxWidthForCardView="800px"
        />
      </div>
      <Pagination
        activePage={pagination.page}
        handlePagination={handlePagination}
        limit={pagination.limit}
        totalCount={pagination.totalData || 0}
      />
    </PrinterContainerStyled>
  );
};

export default Symbols;
